import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Page from '../../components/Page'
import { Badge, Button, Image, Input, Select, Space, Spin, Table, Tag, Typography, Grid } from 'antd'
import VerificationModal from './VerificationModal'
import styled from 'styled-components'
import ChatModal from './ChatModal'

const { Text, Title } = Typography
const { Option } = Select
const { useBreakpoint } = Grid

const HeaderWrap = styled.div`
  margin-bottom: 24px;
`
const HeaderTitle = styled(Title)`
  && {
    margin-bottom: 0;
  }
`
const MobileGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  align-items: stretch;
`
const MobileCard = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr;
  grid-template-rows: auto auto;
  grid-template-areas: 'bild info' 'buttons buttons';
  gap: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
  height: 100%;
  box-sizing: border-box;
`
const ThumbImg = styled(Image)`
  && {
    object-fit: cover;
    border-radius: 6px;
    grid-area: bild;
  }
`
const InfoGrid = styled.div`
  display: grid;
  gap: 4px;
  grid-area: info;
`
const RowWrap = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`
const SecondarySmall = styled(Text)`
  && {
    font-size: 12px;
  }
`
const ButtonsGrid = styled.div`
  display: grid;
  gap: 4px;
  grid-area: buttons;
  justify-content: center;
  align-content: center;
  padding: 0 12px;
`

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
`

const ControlsWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`

const Field = styled.div`
  display: grid;
  gap: 6px;
`

const EmptyWrap = styled.div`
  text-align: center;
  padding: 24px 0 48px;
`

const STATUS_META = {
  open: { color: 'blue', label: i18n('verification.status.open') },
  inReview: { color: 'orange', label: i18n('verification.status.inReview') },
  needsInfo: { color: 'orange', label: i18n('verification.status.needsInfo') },
  approved: { color: 'green', label: i18n('verification.status.verified') },
  rejected: { color: 'red', label: i18n('verification.status.rejected') },
  closed: { color: 'default', label: i18n('verification.status.closed') },
}

const normalizeStatus = s => {
  if (!s) return 'unknown'
  if (s === 'inReview' || s === 'in_review') return 'in_review'
  if (s === 'needsInfo' || s === 'needs_info') return 'needs_info'
  return String(s).toLowerCase()
}

const getPreview = req => {
  const first = Array.isArray(req?.submittedImages) && req.submittedImages.length ? req.submittedImages[0] : null
  return req?.watch?.profileImage || req?.watch?.thumbnailImage || first?.previewUrl || null
}

export default function VerificationPage() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState([])
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('all')
  const [selectedId, setSelectedId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)

  const fetchList = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${global.WatchStorage.backendUrl}/api/v1/admin/verification-requests`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${global.WatchStorage.session.token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setList(Array.isArray(data) ? data : [])
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
  }, [])

  /**
   * HOR-92: Deep Link /verification?open=<verificationId> (aus Benachrichtigungs-Mails) oeffnet die
   * Verifizierung direkt – unabhaengig von Liste und Filtern: VerificationModal laedt sein Detail
   * selbst per ID (404 -> Hinweis im Modal). Der Session-Bootstrap in main.jsx laeuft VOR dem
   * Rendern der Routen, die Query ueberlebt ihn; ohne Session wandert sie als ?redirect= ueber den
   * Login zurueck (utils/loginRedirect, F1). Der Parameter wird sofort aus der URL entfernt,
   * damit ein Reload das Modal nicht erneut oeffnet (gleiches Muster wie pages/Services).
   */
  const [searchParams] = useSearchParams()
  const deepLinkHandledRef = useRef(false)

  useEffect(() => {
    if (deepLinkHandledRef.current) return

    deepLinkHandledRef.current = true

    const raw = searchParams.get('open')

    if (raw === null) return

    window.history.replaceState(null, '', window.location.pathname)

    const id = Number(raw)

    if (!Number.isInteger(id) || id <= 0) return

    setSelectedId(id)
    setIsModalOpen(true)
  }, [])

  const screens = useBreakpoint()
  const isMobile = !screens.md

  const filtered = useMemo(() => {
    let items = list
    if (status !== 'all') {
      items = items.filter(it => normalizeStatus(it?.status) === status)
    }
    if (!q) return items

    const needle = q.toLowerCase()
    const includes = s =>
      String(s || '')
        .toLowerCase()
        .includes(needle)
    const onlyDigits = /^\d+$/.test(needle)

    return items.filter(item => {
      const w = item?.watch || {}
      const ref = w?.referenceNumber || {}
      const brand = ref.make
      const model = ref.model
      const refNum = ref.number
      const serial = w.serialNumber

      const requester = item?.requester || {}
      const requesterName = requester.name
      const requesterEmail = requester.email

      const matchesRequester = includes(requesterName) || includes(requesterEmail)
      const matchesRefData = includes(brand) || includes(model) || includes(refNum)

      const matchesSerial = onlyDigits
        ? String(serial || '')
            .toLowerCase()
            .startsWith(needle)
        : includes(serial)

      const matchesId = String(item?.id || '').includes(needle)

      return matchesRequester || matchesRefData || matchesSerial || matchesId
    })
  }, [list, q, status])

  const columns = [
    {
      title: i18n('verification.columns.image'),
      key: 'preview',
      width: 96,
      render: (_, req) => {
        const src = getPreview(req)
        return (
          <Image
            src={src}
            alt={req?.watch?.name || i18n('common.image')}
            width={72}
            height={72}
            style={{ objectFit: 'cover', borderRadius: 6 }}
            fallback={null}
            preview={false}
          />
        )
      },
    },
    {
      title: i18n('verification.columns.brand'),
      dataIndex: 'id',
      key: 'make',
      width: 95,
      sorter: (a, b) => String(a?.watch?.referenceNumber?.make || '').localeCompare(String(b?.watch?.referenceNumber?.make || '')),
      render: (_, req) => {
        const name = req?.watch?.referenceNumber.make || i18n('common.unknown')
        const ref = req?.watch?.reference ? ` • Ref. ${req.watch.reference}` : ''
        return (
          <span title={`${name}${ref}`}>
            {name}
            <Text type="secondary">{ref}</Text>
          </span>
        )
      },
    },
    {
      title: i18n('verification.columns.model'),
      key: 'model',
      ellipsis: true,
      width: 146,
      sorter: (a, b) => String(a?.watch?.referenceNumber?.model || '').localeCompare(String(b?.watch?.referenceNumber?.model || '')),
      render: (_, req) => {
        const name = req?.watch?.referenceNumber.model || i18n('common.unknown')
        const ref = req?.watch?.reference ? ` • Ref. ${req.watch.reference}` : ''
        return (
          <span title={`${name}${ref}`}>
            {name}
            <Text type="secondary">{ref}</Text>
          </span>
        )
      },
    },
    {
      title: i18n('verification.columns.serial'),
      key: 'serial',
      ellipsis: true,
      width: 106,
      render: (_, req) => req?.watch?.serialNumber || '–',
      sorter: (a, b) => Number(a.watch.serialNumber) - Number(b.watch.serialNumber),
      responsive: ['md'],
    },
    {
      title: i18n('verification.columns.images'),
      key: 'imagesCount',
      width: 100,
      render: (_, req) => {
        const n = Array.isArray(req?.submittedImages) ? req.submittedImages.length : 0
        return <Tag>{i18n('common.imageCount', { count: n })}</Tag>
      },
      responsive: ['sm'],
    },
    {
      title: i18n('verification.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: i18n('verification.status.open'), value: 'open' },
        { text: i18n('verification.status.inReview'), value: 'in_review' },
        { text: i18n('verification.status.needsInfo'), value: 'needs_info' },
        { text: i18n('verification.status.approved'), value: 'approved' },
        { text: i18n('verification.status.rejected'), value: 'rejected' },
        { text: i18n('verification.status.closed'), value: 'closed' },
      ],
      onFilter: (val, record) => normalizeStatus(record.status) === val,
      render: val => <Tag color={STATUS_META[val]?.color || 'default'}>{i18n(`verification.status.${val}`) || i18n('common.unknown')}</Tag>,
    },
    {
      title: i18n('verification.columns.requestedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: val => moment(val).format('L LT').replaceAll('/', '.'),
      width: 150,
    },
    {
      title: i18n('verification.columns.requester'),
      key: 'requester',
      ellipsis: true,
      width: 180,
      sorter: (a, b) => {
        const an = String(a?.requester?.name || a?.requester?.email || '').trim()
        const bn = String(b?.requester?.name || b?.requester?.email || '').trim()
        return an.localeCompare(bn, 'de', { sensitivity: 'base' })
      },
      render: (_, req) => {
        const r = req?.requester
        return r ? (
          <span title={`${r.name || r.email}${r.email ? ` • ${r.email}` : ''}`}>
            {r.name || r.email}
            {r.email ? <Text type="secondary"> • {r.email}</Text> : null}
          </span>
        ) : (
          <Text type="secondary">–</Text>
        )
      },
      responsive: ['md'],
    },
    {
      title: i18n('common.actions'),
      key: 'actions',
      width: 160,
      render: (_, req) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setSelectedId(req.id)
              setIsModalOpen(true)
            }}
          >
            {i18n('common.open')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setSelectedId(req.id)
              setIsChatOpen(true)
            }}
          >
            {i18n('common.chat')}
          </Button>
        </Space>
      ),
    },
  ]

  const MobileList = ({ data }) => {
    return (
      <MobileGrid>
        {data.map(req => {
          const src = getPreview(req)
          return (
            <MobileCard key={req.id}>
              <ThumbImg src={src} alt={req?.watch?.name || i18n('common.image')} width={72} height={72} fallback={null} preview={false} />
              <InfoGrid>
                <RowWrap>
                  <Text strong>#{req.id}</Text>
                  <Tag color={STATUS_META[req.status]?.color || 'default'}>{i18n(`verification.status.${req.status}`) || i18n('common.unknown')}</Tag>
                </RowWrap>
                <div style={{ lineHeight: 1.3 }}>
                  <Text>
                    {req?.watch?.name || i18n('common.watch')} {req?.watch?.reference ? <Text type="secondary">• Ref. {req.watch.reference}</Text> : null}
                  </Text>
                </div>
                <SecondarySmall type="secondary">{new Date(req.createdAt).toLocaleString()}</SecondarySmall>
              </InfoGrid>
              <ButtonsGrid>
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setSelectedId(req.id)
                      setIsModalOpen(true)
                    }}
                  >
                    {i18n('common.open')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedId(req.id)
                      setIsChatOpen(true)
                    }}
                  >
                    {i18n('common.chat')}
                  </Button>
                </Space>
              </ButtonsGrid>
            </MobileCard>
          )
        })}
      </MobileGrid>
    )
  }

  return (
    <Page title={i18n('verification.title')}>
      <Wrapper>
        <HeaderWrap>
          <HeaderTitle level={5}>{i18n('verification.subtitle')}</HeaderTitle>
        </HeaderWrap>

        <div style={{ marginBottom: 16 }}>
          <ControlsWrap>
            <Field>
              <Text>{i18n('common.search')}</Text>
              <Input placeholder={i18n('verification.searchPlaceholder')} value={q} onChange={e => setQ(e.target.value)} onPressEnter={fetchList} allowClear />
            </Field>
            <Field>
              <Text>{i18n('verification.columns.status')}</Text>
              <Select value={status} onChange={setStatus} style={{ minWidth: 160 }}>
                <Option value="all">{i18n('common.all')}</Option>
                <Option value="open">{i18n('verification.status.open')}</Option>
                <Option value="in_review">{i18n('verification.status.inReview')}</Option>
                <Option value="needs_info">{i18n('verification.status.needsInfo')}</Option>
                <Option value="approved">{i18n('verification.status.approved')}</Option>
                <Option value="rejected">{i18n('verification.status.rejected')}</Option>
                <Option value="closed">{i18n('verification.status.closed')}</Option>
              </Select>
            </Field>
            <div>
              <Button onClick={fetchList}>{i18n('common.refresh')}</Button>
            </div>
          </ControlsWrap>
        </div>

        {loading ? (
          <Spin />
        ) : filtered.length === 0 ? (
          <EmptyWrap>
            <Text type="secondary">{i18n('verification.noResults')}</Text>
          </EmptyWrap>
        ) : isMobile ? (
          <MobileList data={filtered} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filtered}
            size="middle"
            tableLayout="fixed"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: t => i18n('common.entries', { count: t }) }}
            scroll={window.innerWidth <= '1500px' ? undefined : { x: 720 }}
          />
        )}

        {isModalOpen && selectedId && (
          <VerificationModal
            make={(() => {
              const req = filtered.find(x => x.id === selectedId)
              return req?.watch?.referenceNumber?.make || ''
            })()}
            model={(() => {
              const req = filtered.find(x => x.id === selectedId)
              return req?.watch?.referenceNumber?.model || ''
            })()}
            id={selectedId}
            onClose={() => {
              setIsModalOpen(false)
              setSelectedId(null)
            }}
          />
        )}
        {isChatOpen && selectedId && (
          <ChatModal
            make={(() => {
              const req = filtered.find(x => x.id === selectedId)
              return req?.watch?.referenceNumber?.make || ''
            })()}
            model={(() => {
              const req = filtered.find(x => x.id === selectedId)
              return req?.watch?.referenceNumber?.model || ''
            })()}
            isOpen={isChatOpen}
            requestId={selectedId}
            onClose={() => {
              setIsChatOpen(false)
              setSelectedId(null)
            }}
          />
        )}
      </Wrapper>
    </Page>
  )
}
